import { MW_APP_BODY_CLASSNAME, MW_APP_HIDE_COOKIE, MW_APP_UTM_SOURCE } from './constants'

/**
 * Set a cookie in the document with a specified expiration time and path.
 * @param name
 * @param value
 * @param expirationDays
 * @param path
 */
export const setDocumentCookie = (
  name: string,
  value: string,
  expirationDays: number,
  path = '/',
): void => {
  const date = new Date()
  date.setTime(date.getTime() + (expirationDays * 24 * 60 * 60 * 1000))
  const expires = `expires=${date.toUTCString()}`
  document.cookie = `${name}=${value};${expires};path=${path}`
}
/**
 * Gets a cookie value by its name from the document cookies
 * @param {string} cookieName - The name of the cookie to retrieve
 * @returns {string|null} The cookie value or null if not found
 */
export const getCookieValueByName = (cookieName: string): string | null => {
  const decodedCookie = decodeURIComponent(document.cookie)
  const cookieArray = decodedCookie.split(';')

  for (const cookieItem of cookieArray) {
    const [name, value] = cookieItem.trim()
      .split('=')
    if (name === cookieName) {
      return value
    }
  }
  return null
}

/**
 * Handles the app view functionality by managing cookies and modifying
 * the DOM based on certain conditions.
 *
 * This function checks the URL for a specific `utm_source` parameter and
 * determines whether to display or hide a certain feature in the application.
 * It sets a cookie if the `utm_source` matches a predefined constant and
 * the cookie does not already exist. Additionally, it adds a class to the
 * document body if the conditions are met.
 *
 * Behavior:
 * - Checks for the `utm_source` query parameter in the URL.
 * - Reads an existing cookie value to determine the state.
 * - Sets a new cookie to hide the specific feature if criteria are satisfied.
 * - Adds a CSS class to the document body when required.
 *
 * NOTE: ideally we should be looking at headers, but we would need access to the
 * http request for that. This is the quick-and-dirty way to determine if the request
 * is from the mobile app
 */
export const handleAppView = (): void => {
  const search = new window.URLSearchParams(window.location.search)
  const utmSource = search.get('utm_source')
  const existingCookie = getCookieValueByName(MW_APP_HIDE_COOKIE)

  if (utmSource === MW_APP_UTM_SOURCE && !existingCookie) {
    // expires one day from now
    setDocumentCookie(MW_APP_HIDE_COOKIE, 'true', 1, '/')
  }

  if (utmSource === MW_APP_UTM_SOURCE || existingCookie === 'true') {
    document.body.classList.add(MW_APP_BODY_CLASSNAME)
  }
}
